import { useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Eye, Sparkles, Video } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const conditionMap = { new: 'Novo', used: 'Usado' };

function isPromoted(product) {
  return product.is_promoted && product.promoted_until && new Date(product.promoted_until) > new Date();
}

export default function ProductCard({ product }) {
  const imageUrl = product.images?.[0]
    ? (product.images[0].startsWith('http') ? product.images[0] : `${API_URL}/api/files/${product.images[0]}`)
    : 'https://images.unsplash.com/photo-1592041275490-dcac548bad2e?w=400&h=400&fit=crop';

  const videoUrl = product.video
    ? (product.video.startsWith('http') ? product.video : `${API_URL}/api/files/${product.video}`)
    : null;

  const promoted = isPromoted(product);
  const videoRef = useRef(null);
  const [hovering, setHovering] = useState(false);

  const handleMouseEnter = useCallback(() => {
    if (!videoUrl) return;
    setHovering(true);
    videoRef.current?.play().catch(() => {});
  }, [videoUrl]);

  const handleMouseLeave = useCallback(() => {
    if (!videoUrl) return;
    setHovering(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [videoUrl]);

  return (
    <Link
      to={`/products/${product._id}`}
      className={`flex flex-col rounded-lg border bg-card overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer group ${promoted ? 'border-amber-300 ring-1 ring-amber-200 shadow-md shadow-amber-100/50' : 'border-border hover:border-primary/20'}`}
      data-testid={`product-card-${product._id}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative aspect-square overflow-hidden bg-muted">
        <img
          src={imageUrl}
          alt={product.title}
          className={`object-cover w-full h-full group-hover:scale-105 transition-all duration-300 ${hovering && videoUrl ? 'opacity-0' : 'opacity-100'}`}
          loading="lazy"
        />
        {videoUrl && (
          <video
            ref={videoRef}
            src={hovering ? videoUrl : undefined}
            muted
            loop
            playsInline
            preload="none"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${hovering ? 'opacity-100' : 'opacity-0'}`}
            data-testid={`product-card-video-${product._id}`}
          />
        )}
        <div className="absolute top-2 left-2 flex gap-1">
          {promoted && (
            <Badge className="bg-gradient-to-r from-amber-400 to-orange-400 text-white border-0 text-[10px] gap-1 shadow-sm">
              <Sparkles className="h-2.5 w-2.5" /> Destaque
            </Badge>
          )}
        </div>
        <div className="absolute top-2 right-2 flex gap-1">
          {videoUrl && (
            <Badge variant="secondary" className="text-[10px] bg-black/70 text-white border-0 backdrop-blur-sm gap-1" data-testid={`video-badge-${product._id}`}>
              <Video className="h-2.5 w-2.5" /> Vídeo
            </Badge>
          )}
          {product.condition && (
            <Badge variant="secondary" className="text-[10px] bg-white/90 text-foreground backdrop-blur-sm">
              {conditionMap[product.condition] || product.condition}
            </Badge>
          )}
        </div>
      </div>
      <div className="p-3 flex flex-col gap-1.5 flex-1">
        <h3 className="text-sm font-medium line-clamp-2 leading-snug group-hover:text-primary transition-colors">
          {product.title}
        </h3>
        <p className="text-lg font-bold text-primary tracking-tight" style={{ fontFamily: 'Outfit' }}>
          R$ {Number(product.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </p>
        <div className="flex items-center justify-between mt-auto pt-1.5">
          {product.location && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" /> {product.location}
            </span>
          )}
          {product.views > 0 && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Eye className="h-3 w-3" /> {product.views}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
